import * as THREE from 'three/webgpu';
import type {SketchSolverIslandSnapshot} from '../../../history/solver/SketchSolverRecordTypes';
import type {AbstractSketchEdgeObject} from '../../../mesh/sketch/AbstractSketchEdgeObject';
import type {ISketchObject} from '../../../mesh/sketch/ISketchObject';
import type {SketchPointObject} from '../../../mesh/sketch/SketchPointObject';
import {isSketchSplineObject} from '../../../mesh/guards/isSketchSplineObject';
import {isSketchStyleSplineObject} from '../../../mesh/guards/isSketchStyleSplineObject';
import {resolveSketchEntityParameterAdapter} from '../adapters/SketchEntityParameterAdapters';
import type {CurveOffsetDerivedSyncService} from '../../dimension/derived/CurveOffsetDerivedSyncService';
import type {
    SketchParametricRuntimeApplyOptions,
    SketchParametricRuntimeApplyResult,
    SketchSolvedEntityParameters,
} from '../../../../types/sketch/solver/SketchParametricRuntimeTypes';
import {refreshSlotOwnersDerivedVisuals} from '../../slot/refreshSlotOwnersDerivedVisuals';

/**
 * 参数解运行时写回适配器。
 *
 * 设计说明：
 * 1. 写回协议统一覆盖点位、实体非点参数与派生几何刷新：
 *    `syncPositionSilently` 批量静默写点，再对去重后的关联边
 *    `refreshGeometryOnly`；
 * 2. 输入是**已求解**的 island 快照（`solveSketchParametricIsland` 输出且
 *    `applied === true`），本类只做局部坐标 → 世界坐标换算与写回，不做求解；
 * 3. 点参数先静默写入，实体半径等非点参数随后通过实体参数适配器写入，
 *    最后由调用方统一刷新受影响边和复合宿主派生视觉。
 */
export class SketchParametricRuntimeApplier {
    /** 等距样条派生同步服务（可注入；未注入时跳过派生同步）。 */
    private curveOffsetDerivedSync: CurveOffsetDerivedSyncService | null = null;

    /**
     * 把求解后的 island 点位写回草图运行时点对象。
     *
     * @param island 已求解的 island 快照。
     * @param runtimePoints 运行时点对象集合（uuid 与 island pointId 对应）。
     * @param options
     * @returns 写回结果。
     */
    public applyPointPositions(
        island: SketchSolverIslandSnapshot,
        runtimePoints: readonly SketchPointObject[],
        options?: SketchParametricRuntimeApplyOptions,
    ): SketchParametricRuntimeApplyResult {
        const runtimePointById = new Map(runtimePoints.map((point) => [point.uuid, point]));
        const affectedEdges = new Map<string, AbstractSketchEdgeObject>();
        const missingPointIds: string[] = [];
        let updatedPointCount = 0;

        island.pointStates.forEach((pointState) => {
            const point = runtimePointById.get(pointState.pointId);

            if (!point) {
                missingPointIds.push(pointState.pointId);
                return;
            }

            const nextWorld = point.referencePlane.localToWorld(
                new THREE.Vector2(pointState.localPosition[0], pointState.localPosition[1]),
            );

            if (point.getPosition().equals(nextWorld)) {
                return;
            }
            point.syncPositionSilently(nextWorld);
            updatedPointCount += 1;
            point.getConnectedEdgesSnapshot().forEach((edge) => {
                affectedEdges.set(edge.uuid, edge);
            });
        });

        if (options?.deferEdgeRefresh === true) {
            /**
             * 延迟刷边：调用方需在**实体参数写回之后**再统一刷新。
             * 弧的 `refreshGeometryOnly` 在存在半径关系视图时按"定义半径"做锁定重建，
             * 此刻定义还是旧半径——先刷边会把刚解好的端点吸回旧圆周
             * （实测端点永远停在"上一轮"的半径上，滞后一拍）。
             */
            return {
                updatedPointCount,
                refreshedEdgeCount: 0,
                missingPointIds,
                affectedEdges,
            };
        }
        this.refreshAffectedEdges(affectedEdges);

        return {
            updatedPointCount,
            refreshedEdgeCount: affectedEdges.size,
            missingPointIds,
            affectedEdges,
        };
    }

    /**
     * 统一刷新被写回波及的边与复合宿主派生视觉。
     *
     * @param affectedEdges 几何发生变化的边。
     */
    public refreshAffectedEdges(affectedEdges: ReadonlyMap<string, AbstractSketchEdgeObject>): void {
        affectedEdges.forEach((edge) => {
            // 边自身先完成同步刷新；集合/槽口轮廓由循环结束后按宿主去重刷新一次。
            edge.refreshGeometryOnly(undefined, {compositeVisualRefreshMode: 'deferred'});
        });
        this.refreshCompositeOwnersOf(affectedEdges);
        // 等距样条派生同步：被编辑曲线写回后，重算其偏移对侧曲线（异步重采样落地后还会二次同步）。
        this.curveOffsetDerivedSync?.synchronizeAffectedEdges(affectedEdges.values());
    }

    /**
     * 注入等距样条派生同步服务；传 null 清除。
     *
     * @param service 派生同步服务。
     */
    public configureCurveOffsetDerivedSync(service: CurveOffsetDerivedSyncService | null): void {
        this.curveOffsetDerivedSync = service;
    }

    /**
     * 淘汰一次失败事务中已经排队、但尚未落地的异步派生几何。
     *
     * 样条刷新会把 Worker 结果延后写回；仅恢复控制点并再次刷新，会让“回滚刷新”
     * 成为最新任务，仍可能在事务结束后覆盖 before 曲线定义。回滚完成后统一推进实体
     * 修订号，不再排新任务，保证失败事务的任何异步结果都没有写权限。
     *
     * @param affectedEdges 本次失败写回触及的边。
     */
    public invalidatePendingAsyncGeometry(
        affectedEdges: ReadonlyMap<string, AbstractSketchEdgeObject>,
    ): void {
        affectedEdges.forEach((edge) => {
            if (isSketchSplineObject(edge) || isSketchStyleSplineObject(edge)) {
                edge.invalidatePendingCurveRebuild();
            }
        });
    }

    /**
     * 刷新被写回点位波及的复合实体派生视觉（中心线辅助虚线 + 上色轮廓）。
     *
     * 派生视觉不会自己跟着点走，边级 `refreshGeometryOnly` 也管不到宿主级的
     * 填充网格与中心线——漏刷即表现为旧弧度线条与新几何叠加显示。
     * 实现与 Undo/Redo 恢复共用一份（`refreshSlotOwnersDerivedVisuals`）。
     *
     * @param affectedEdges 本次写回中几何发生变化的边。
     */
    private refreshCompositeOwnersOf(affectedEdges: ReadonlyMap<string, AbstractSketchEdgeObject>): void {
        refreshSlotOwnersDerivedVisuals(affectedEdges.values());
    }

    /**
     * 把求解后的实体非点参数写回运行时实体。
     *
     * 说明：参数求解的自由度不只有点（圆/弧半径即是），此前这些解被直接丢弃，
     * 表现为"改半径/直径尺寸后几何不动且不报错"。写回按 entityKind 派发到实体参数
     * 适配器，实体自己决定怎么改自身几何。
     *
     * @param solvedEntityParameters 求解后的实体参数（持久 ID 与运行时 uuid 均可作键）。
     * @param runtimeEntities 运行时实体集合。
     * @returns 实际发生几何变更的实体数量。
     */
    public applyEntityParameters(
        solvedEntityParameters: ReadonlyMap<string, SketchSolvedEntityParameters>,
        runtimeEntities: readonly ISketchObject[],
    ): number {
        if (solvedEntityParameters.size === 0) {
            return 0;
        }

        let updatedEntityCount = 0;

        runtimeEntities.forEach((entity) => {
            const parameters = solvedEntityParameters.get(entity.uuid)
                ?? solvedEntityParameters.get(String(Reflect.get(entity, 'persistentId') ?? ''));

            if (!parameters) {
                return;
            }

            const adapter = resolveSketchEntityParameterAdapter(entity);

            if (adapter?.applyParameters(entity, parameters) === true) {
                updatedEntityCount += 1;
            }
        });

        return updatedEntityCount;
    }

    /**
     * 读取实体当前的非点参数（回滚快照）。
     *
     * @param runtimeEntities 运行时实体集合。
     * @returns 实体 uuid → 当前参数；无非点自由度的实体不入表。
     */
    public captureEntityParameters(
        runtimeEntities: readonly ISketchObject[],
    ): ReadonlyMap<string, SketchSolvedEntityParameters> {
        const captured = new Map<string, SketchSolvedEntityParameters>();

        runtimeEntities.forEach((entity) => {
            const adapter = resolveSketchEntityParameterAdapter(entity);

            if (adapter) {
                captured.set(entity.uuid, adapter.readParameters(entity));
            }
        });

        return captured;
    }
}
